/**
 * Error-report builder.
 *
 * Three call sites consume this helper:
 *
 *  1. The `window.onerror` / `unhandledrejection` global handlers
 *  installed in `main.tsx`. They produce a non-blocking toast
 *  whose "Copy report" action puts the JSON below on the
 *  clipboard so the user can paste a meaningful bug report.
 *  2. The `<GlobalErrorBoundary>` recovery screen — its "Copy
 *  error report" button uses the same shape so the user's
 *  paste-back stays consistent across surfaces.
 *  3. `localStorage` persistence ("mime-inspector:last-error")
 *  done by the boundary's `componentDidCatch`, so the next
 *  session can offer a "Restore last error context" action.
 *
 * No telemetry — nothing leaves the device. The report is for the
 * user to share manually (bug filing, support chat, paste-back
 * into a Discord thread).
 */

import { APP_VERSION } from "../build-version";
import { writeClipboard } from "./clipboard";
import { STORAGE_KEYS } from "./storageKeys";

/**
 * The shape of a single error report. Kept narrow on purpose — every
 * field is either a literal primitive or a string the user can read
 * before pasting. We deliberately do NOT include the parsed message,
 * any header values, attachment names, or any other potentially
 * private state from the active session.
 */
export interface ErrorReport {
  msg: string;
  stack: string;
  ua: string;
  /** ISO-8601 UTC timestamp at the moment the report was built. */
  time: string;
  /** Current URL (with hash + query, so the user can reproduce). */
  url: string;
  /** `APP_VERSION` from `src/build-version.ts`. */
  appVersion: string;
  /**
   * IETF BCP 47 locale tag in effect (`navigator.language`) at the moment the
   * report was built. Helps a triager reproduce a locale-specific crash
   * without asking the reporter to dig through Settings. Read defensively
   * because some sandboxed contexts (jsdom, certain WebViews) leave the
   * property undefined.
   */
  locale: string;
}

/**
 * LocalStorage key under which the GlobalErrorBoundary persists its
 * last captured error. Exported so tests + a future "Restore last
 * error context" surface can read it.
 */
export const LAST_ERROR_STORAGE_KEY = STORAGE_KEYS.lastError;
/** Pre-namespace key, read as a fallback then cleared with the new key. */
const LEGACY_LAST_ERROR_KEY = "mime-inspector:last-error";

/**
 * Build a structured error report. Safe to call from anywhere: never
 * throws — the catch handlers fall back to defaults when one of the
 * accessor APIs (location, navigator) is unavailable (jsdom, very
 * old browsers, certain sandboxed contexts).
 */
export function buildErrorReport(error: unknown): ErrorReport {
  const err = normaliseError(error);
  let url = "";
  try {
    if (typeof window !== "undefined" && window.location) {
      url = window.location.href;
    }
  } catch {
    /* sandbox / cross-origin */
  }
  let ua = "";
  try {
    if (typeof navigator !== "undefined" && navigator.userAgent) {
      ua = navigator.userAgent;
    }
  } catch {
    /* no navigator */
  }
  let locale = "";
  try {
    if (typeof navigator !== "undefined" && navigator.language) {
      locale = navigator.language;
    }
  } catch {
    /* sandboxed navigator */
  }
  let time: string;
  try {
    time = new Date().toISOString();
  } catch {
    /* clock corruption — vanishingly rare */
    time = String(Date.now());
  }
  return {
    msg: err.message,
    stack: err.stack ?? "(no stack available)",
    ua,
    time,
    url,
    appVersion: APP_VERSION,
    locale,
  };
}

/** Coerce arbitrary thrown values into an `Error`-like shape. */
function normaliseError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || "Unknown error",
      stack: error.stack,
    };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

/**
 * Stringify the report as pretty-printed JSON. Standalone so callers
 * can compose their own framing (e.g. the boundary prepending an
 * intro line) before writing to clipboard.
 */
export function formatErrorReport(report: ErrorReport): string {
  return JSON.stringify(report, null, 2);
}

/**
 * Persist the report under {@link LAST_ERROR_STORAGE_KEY}. Best
 * effort — quota / private-mode failures are swallowed (we don't want
 * a storage write to escalate into yet another error). Returns true
 * when the write succeeded; tests + the recovery surface use it to
 * decide whether to show a "Restore last error context" CTA on the
 * next mount.
 */
export function persistLastError(report: ErrorReport): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    window.localStorage.setItem(LAST_ERROR_STORAGE_KEY, formatErrorReport(report));
    return true;
  } catch {
    /* quota / private mode / disabled storage */
    return false;
  }
}

/**
 * Read back the most recently persisted error, if any. Tolerates
 * malformed JSON (returns null) so a manual edit can't crash the
 * recovery surface.
 */
export function readLastError(): ErrorReport | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw =
      window.localStorage.getItem(LAST_ERROR_STORAGE_KEY) ??
      window.localStorage.getItem(LEGACY_LAST_ERROR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const r = parsed as Partial<ErrorReport>;
    if (typeof r.msg !== "string") return null;
    return {
      msg: r.msg,
      stack: typeof r.stack === "string" ? r.stack : "",
      ua: typeof r.ua === "string" ? r.ua : "",
      time: typeof r.time === "string" ? r.time : "",
      url: typeof r.url === "string" ? r.url : "",
      appVersion: typeof r.appVersion === "string" ? r.appVersion : "",
      // `locale` was added later; tolerate older persisted shapes by
      // defaulting to "" rather than rejecting the record.
      locale: typeof r.locale === "string" ? r.locale : "",
    };
  } catch {
    return null;
  }
}

/**
 * Clear the persisted report. Used by the GlobalErrorBoundary's
 * "Clear state and refresh" action and by tests.
 */
export function clearLastError(): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(LAST_ERROR_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_LAST_ERROR_KEY);
  } catch {
    /* quota / private mode */
  }
}

/**
 * Write the JSON report to the system clipboard. Returns a Promise
 * that resolves to `true` when the write succeeded, `false`
 * otherwise. Callers MUST react to the answer: this is the report's
 * only route off the device, so a caller that drops the `false` leaves
 * the reader believing they hold a bug report they do not.
 */
export async function copyErrorReport(report: ErrorReport): Promise<boolean> {
  return (await writeClipboard(formatErrorReport(report))) === "written";
}
